// Aviah Insights — blog data model per editorial spec.
// status: "published" (full article) | "draft" (structured editorial draft)

export const CATEGORIES = {
  taxes: { en: "Taxes & Accounting", es: "Impuestos y Contabilidad" },
  business: { en: "Business", es: "Negocios" },
  education: { en: "Financial Education", es: "Educación Financiera" },
  protection: { en: "Asset Protection", es: "Protección de Activos" },
  estate: { en: "Estate & Legacy", es: "Patrimonio y Legado" },
  investments: { en: "Investments", es: "Inversiones" },
  immigration: { en: "Immigration", es: "Inmigración" },
  entrepreneurship: { en: "Entrepreneurship", es: "Emprendimiento" },
  news: { en: "News & Updates", es: "Noticias y Actualizaciones" },
};

const AUTHOR = {
  name: "Escarling Martínez",
  role: { en: "Founder & CEO", es: "Fundadora y CEO" },
  bio: {
    en: "Escarling Martínez is the Founder & CEO of Aviah Financial. Her career spans insurance (MetLife), corporate operations (TJX Companies, Boston), hospital administration (Mass General Hospital) and a degree in Accounting and Business Administration.",
    es: "Escarling Martínez es Fundadora y CEO de Aviah Financial. Su trayectoria abarca seguros (MetLife), operaciones corporativas (TJX Companies, Boston), administración hospitalaria (Mass General Hospital) y una licenciatura en Contabilidad y Administración de Empresas.",
  },
};

export const POSTS = [
  {
    slug: "tax-preparation-vs-tax-planning",
    status: "published",
    category: "taxes",
    img: "office",
    date: "2026-06-02",
    updated: "2026-06-02",
    reviewed: "2026-06-02",
    reading: 5,
    author: AUTHOR,
    en: {
      title: "Tax Preparation vs. Tax Planning: What's the Difference?",
      excerpt:
        "One looks backward at what already happened. The other looks forward at what you can still shape. Understanding the difference changes how much control you have.",
      content: [
        {
          h: "Two words that sound the same — and aren't",
          p: [
            "Tax preparation is the act of reporting what already happened: gathering documents, completing forms and filing an accurate return for a year that is over. It is necessary, and it must be done carefully — but by the time preparation begins, nearly every decision that shaped the result has already been made.",
            "Tax planning is the conversation that happens before the year ends. It asks: given your income, your business, your family and your goals, which decisions made now would put you in a better position later?",
          ],
        },
        {
          h: "Preparation reports. Planning decides.",
          p: [
            "Think of preparation as the scorekeeper and planning as the strategy. A scorekeeper records the game faithfully, but cannot change the score. Strategy happens during the game — while there is still time to act.",
            "For employees with a single W-2, preparation may be most of what is needed. But the moment you add a business, investments, rental income, a side activity or a major life change, planning becomes the difference between reporting a surprise and shaping an outcome.",
          ],
        },
        {
          h: "Who benefits most from planning",
          p: [
            "Business owners feel it first: structure choices, expense timing and how you pay yourself all have tax consequences that cannot be undone in April. Families with multiple income sources, new U.S. residents and anyone approaching a major transaction — selling property, starting a company, receiving an inheritance — benefit the same way.",
          ],
        },
        {
          h: "Planning is a rhythm, not an event",
          p: [
            "Effective tax planning is a short, recurring conversation — a mid-year review, a check before a big decision, a year-end look while there is still time. It does not need to be complicated. It needs to happen before the decisions, not after.",
          ],
        },
        {
          h: "The bottom line",
          p: [
            "Preparation keeps you compliant. Planning keeps you in control. The strongest position is having both — organized records feeding an accurate return, and a strategy that made that return worth filing.",
            "If you have only ever done the first, the second is where the conversation starts.",
          ],
        },
      ],
      faqs: [
        { q: "Is tax planning only for high incomes?", a: "No. Anyone with a business, multiple income sources or a major financial decision ahead can benefit. The scale changes; the principle doesn't." },
        { q: "When should tax planning happen?", a: "Before decisions are made — ideally mid-year and before year-end, not during filing season when the year is already closed." },
      ],
    },
    es: {
      title: "Preparación de Impuestos vs. Planificación Fiscal: ¿Cuál es la Diferencia?",
      excerpt:
        "Una mira hacia atrás, a lo que ya pasó. La otra mira hacia adelante, a lo que aún puedes moldear. Entender la diferencia cambia cuánto control tienes.",
      content: [
        {
          h: "Dos palabras que suenan igual — y no lo son",
          p: [
            "La preparación de impuestos es el acto de reportar lo que ya ocurrió: reunir documentos, completar formularios y presentar una declaración precisa de un año que ya terminó. Es necesaria y debe hacerse con cuidado — pero cuando comienza la preparación, casi todas las decisiones que moldearon el resultado ya se tomaron.",
            "La planificación fiscal es la conversación que ocurre antes de que termine el año. Pregunta: dados tus ingresos, tu negocio, tu familia y tus metas, ¿qué decisiones tomadas ahora te pondrían en una mejor posición después?",
          ],
        },
        {
          h: "La preparación reporta. La planificación decide.",
          p: [
            "Piensa en la preparación como quien anota el marcador y en la planificación como la estrategia. El anotador registra el juego fielmente, pero no puede cambiar el marcador. La estrategia ocurre durante el juego — mientras aún hay tiempo de actuar.",
            "Para empleados con un solo W-2, la preparación puede ser la mayor parte de lo necesario. Pero en el momento en que agregas un negocio, inversiones, ingresos por alquiler, una actividad paralela o un cambio importante de vida, la planificación se convierte en la diferencia entre reportar una sorpresa y moldear un resultado.",
          ],
        },
        {
          h: "Quién se beneficia más de la planificación",
          p: [
            "Los dueños de negocio lo sienten primero: las decisiones de estructura, el momento de los gastos y cómo te pagas a ti mismo tienen consecuencias fiscales que no se pueden deshacer en abril. Las familias con múltiples fuentes de ingreso, los nuevos residentes de EE. UU. y cualquiera que se acerque a una transacción importante — vender una propiedad, iniciar una empresa, recibir una herencia — se benefician de la misma manera.",
          ],
        },
        {
          h: "La planificación es un ritmo, no un evento",
          p: [
            "La planificación fiscal efectiva es una conversación corta y recurrente — una revisión a mitad de año, una pausa antes de una gran decisión, una mirada de fin de año mientras aún hay tiempo. No necesita ser complicada. Necesita ocurrir antes de las decisiones, no después.",
          ],
        },
        {
          h: "En resumen",
          p: [
            "La preparación te mantiene al día. La planificación te mantiene en control. La posición más fuerte es tener ambas — registros organizados alimentando una declaración precisa, y una estrategia que hizo que esa declaración valiera la pena.",
            "Si siempre has hecho solo lo primero, lo segundo es donde comienza la conversación.",
          ],
        },
      ],
      faqs: [
        { q: "¿La planificación fiscal es solo para ingresos altos?", a: "No. Cualquier persona con un negocio, múltiples fuentes de ingreso o una decisión financiera importante por delante puede beneficiarse. La escala cambia; el principio no." },
        { q: "¿Cuándo debe ocurrir la planificación fiscal?", a: "Antes de que se tomen las decisiones — idealmente a mitad de año y antes del cierre del año, no durante la temporada de declaración cuando el año ya cerró." },
      ],
    },
  },
  {
    slug: "what-is-asset-protection",
    status: "published",
    category: "protection",
    img: "marble",
    date: "2026-06-09",
    updated: "2026-06-09",
    reviewed: "2026-06-09",
    reading: 5,
    author: AUTHOR,
    en: {
      title: "What Is Asset Protection and Why Does It Matter?",
      excerpt:
        "Asset protection isn't about hiding anything. It's about organizing what you own so that what took years to build isn't exposed by accident.",
      content: [
        {
          h: "Protection starts with visibility",
          p: [
            "Most people cannot list everything they own, how each asset is titled, and what would happen to it in a lawsuit, a business dispute or a death in the family. That gap — between what you own and how well you understand its structure — is where risk lives.",
            "Asset protection is the discipline of closing that gap: inventorying what you have, understanding how it is structured and organizing it intentionally.",
          ],
        },
        {
          h: "What asset protection is not",
          p: [
            "It is not hiding money, evading obligations or a trick reserved for the very wealthy. Legitimate asset protection is transparent and planned in advance — courts look unfavorably on last-minute maneuvers, which is why timing and honesty matter.",
            "It is also not a guarantee. No structure eliminates all risk, and anyone who promises complete protection is overstating what the law allows.",
          ],
        },
        {
          h: "Why ordinary families need it",
          p: [
            "If you own a home, run a business or have savings, you have something worth organizing. Business owners face the sharpest version of this: without clear separation, a problem in the business can reach into the family's home and savings.",
            "Families with property in more than one country face another layer — different systems, different rules, and documents that must agree with each other.",
          ],
        },
        {
          h: "The connection to everything else",
          p: [
            "How your assets are organized determines how they are taxed, how they are protected and how they transfer to the next generation. That is why asset protection works best evaluated together with your business structure, your taxes and your estate plan — not as a separate errand.",
          ],
        },
        {
          h: "Where to begin",
          p: [
            "Begin with an honest inventory and a simple question: if something went wrong tomorrow, what would actually be exposed? The answer usually makes the next steps obvious. Where legal instruments are required — trusts, entities, agreements — qualified attorneys execute them; our role is the strategy and organization that come first.",
          ],
        },
      ],
      faqs: [
        { q: "Is asset protection legal?", a: "Yes — when done transparently and in advance. It becomes problematic only when used to evade existing obligations, which is why professional guidance matters." },
        { q: "Do I need to be wealthy to benefit?", a: "No. A home, a business or meaningful savings are all worth organizing. The strategy scales with the situation." },
      ],
    },
    es: {
      title: "¿Qué es la Protección de Activos y Por Qué Importa?",
      excerpt:
        "La protección de activos no se trata de esconder nada. Se trata de organizar lo que posees para que lo que tomó años construir no quede expuesto por accidente.",
      content: [
        {
          h: "La protección comienza con la visibilidad",
          p: [
            "La mayoría de las personas no puede enumerar todo lo que posee, cómo está titulado cada activo y qué pasaría con él en una demanda, una disputa comercial o un fallecimiento en la familia. Esa brecha — entre lo que posees y qué tan bien entiendes su estructura — es donde vive el riesgo.",
            "La protección de activos es la disciplina de cerrar esa brecha: inventariar lo que tienes, entender cómo está estructurado y organizarlo intencionalmente.",
          ],
        },
        {
          h: "Lo que la protección de activos no es",
          p: [
            "No es esconder dinero, evadir obligaciones ni un truco reservado para los muy adinerados. La protección legítima de activos es transparente y se planifica con anticipación — los tribunales miran con desaprobación las maniobras de último minuto, por eso el momento y la honestidad importan.",
            "Tampoco es una garantía. Ninguna estructura elimina todo el riesgo, y quien promete protección completa está exagerando lo que la ley permite.",
          ],
        },
        {
          h: "Por qué las familias comunes la necesitan",
          p: [
            "Si posees una casa, tienes un negocio o tienes ahorros, tienes algo que vale la pena organizar. Los dueños de negocio enfrentan la versión más aguda de esto: sin una separación clara, un problema en el negocio puede alcanzar la casa y los ahorros de la familia.",
            "Las familias con propiedades en más de un país enfrentan otra capa — sistemas diferentes, reglas diferentes y documentos que deben concordar entre sí.",
          ],
        },
        {
          h: "La conexión con todo lo demás",
          p: [
            "La forma en que se organizan tus activos determina cómo se gravan, cómo se protegen y cómo se transfieren a la siguiente generación. Por eso la protección de activos funciona mejor evaluada junto con tu estructura empresarial, tus impuestos y tu plan patrimonial — no como un trámite separado.",
          ],
        },
        {
          h: "Por dónde empezar",
          p: [
            "Comienza con un inventario honesto y una pregunta simple: si algo saliera mal mañana, ¿qué quedaría realmente expuesto? La respuesta suele hacer obvios los próximos pasos. Donde se requieren instrumentos legales — fideicomisos, entidades, acuerdos — los abogados calificados los ejecutan; nuestro rol es la estrategia y la organización que vienen primero.",
          ],
        },
      ],
      faqs: [
        { q: "¿Es legal la protección de activos?", a: "Sí — cuando se hace de forma transparente y con anticipación. Solo se vuelve problemática cuando se usa para evadir obligaciones existentes, por eso importa la orientación profesional." },
        { q: "¿Necesito ser adinerado para beneficiarme?", a: "No. Una casa, un negocio o ahorros significativos merecen organización. La estrategia escala con la situación." },
      ],
    },
  },
  {
    slug: "what-is-an-itin",
    status: "published",
    category: "taxes",
    img: "meeting",
    date: "2026-06-16",
    updated: "2026-06-16",
    reviewed: "2026-06-16",
    reading: 4,
    author: AUTHOR,
    en: {
      title: "What Is an ITIN and Who May Need One?",
      excerpt:
        "For many individuals who aren't eligible for a Social Security number, the ITIN is the key that opens the U.S. tax system — and much that depends on it.",
      content: [
        {
          h: "The basics",
          p: [
            "An ITIN — Individual Taxpayer Identification Number — is a nine-digit number issued by the IRS to people who need to file U.S. taxes but are not eligible for a Social Security number. It exists for one purpose: federal tax reporting.",
            "It does not authorize work, change immigration status or provide Social Security benefits. What it does is allow a person to meet their tax obligations properly — which matters more than many realize.",
          ],
        },
        {
          h: "Who may need one",
          p: [
            "Common situations include: a nonresident or resident alien required to file a U.S. return; a spouse or dependent of a U.S. taxpayer who isn't eligible for an SSN; or a foreign investor with U.S. tax reporting obligations.",
            "If anyone in your household earns income that should be reported and doesn't have an SSN, the ITIN is usually the answer.",
          ],
        },
        {
          h: "Why it matters beyond taxes",
          p: [
            "Filing with an ITIN creates a documented history — of income, of residence, of compliance. That record can become important later: when opening certain accounts, applying for credit, or documenting presence and contribution.",
            "Just as important, filing correctly avoids the compounding problems that unreported income creates.",
          ],
        },
        {
          h: "The application — and the shortcut few know about",
          p: [
            "Applications use IRS Form W-7 and require identity documentation — which traditionally meant mailing original passports or waiting for certified copies. A Certified Acceptance Agent (CAA) is authorized by the IRS to verify documents in person, so originals stay with you.",
            "Aviah Financial serves as a Certified Acceptance Agent, which typically makes the process faster, safer and far less stressful.",
          ],
        },
        {
          h: "A word of caution",
          p: [
            "ITINs can expire if unused on a federal return for several consecutive years, and renewal follows a similar process. If you think you or a family member may need one — or need one renewed — a short consultation usually settles the question quickly.",
          ],
        },
      ],
      faqs: [
        { q: "Does an ITIN give me immigration status or work authorization?", a: "No. An ITIN is strictly a tax processing number. It does not change immigration status and does not authorize employment." },
        { q: "What is a Certified Acceptance Agent?", a: "A CAA is authorized by the IRS to verify identity documents in person for ITIN applications, so applicants don't have to mail original documents like passports." },
      ],
    },
    es: {
      title: "¿Qué es un ITIN y Quién Puede Necesitarlo?",
      excerpt:
        "Para muchas personas que no son elegibles para un número de Seguro Social, el ITIN es la llave que abre el sistema fiscal de EE. UU. — y mucho de lo que depende de él.",
      content: [
        {
          h: "Lo básico",
          p: [
            "Un ITIN — Número de Identificación Personal del Contribuyente — es un número de nueve dígitos emitido por el IRS para personas que necesitan declarar impuestos en EE. UU. pero no son elegibles para un número de Seguro Social. Existe con un propósito: la declaración de impuestos federales.",
            "No autoriza el trabajo, no cambia el estatus migratorio ni otorga beneficios del Seguro Social. Lo que sí hace es permitir que una persona cumpla con sus obligaciones fiscales correctamente — lo cual importa más de lo que muchos creen.",
          ],
        },
        {
          h: "Quién puede necesitarlo",
          p: [
            "Las situaciones comunes incluyen: un extranjero residente o no residente que debe presentar una declaración en EE. UU.; un cónyuge o dependiente de un contribuyente estadounidense que no es elegible para un SSN; o un inversionista extranjero con obligaciones fiscales en EE. UU.",
            "Si alguien en tu hogar obtiene ingresos que deben reportarse y no tiene SSN, el ITIN suele ser la respuesta.",
          ],
        },
        {
          h: "Por qué importa más allá de los impuestos",
          p: [
            "Declarar con un ITIN crea un historial documentado — de ingresos, de residencia, de cumplimiento. Ese registro puede volverse importante después: al abrir ciertas cuentas, solicitar crédito o documentar presencia y contribución.",
            "Igual de importante: declarar correctamente evita los problemas acumulativos que crean los ingresos no reportados.",
          ],
        },
        {
          h: "La solicitud — y el atajo que pocos conocen",
          p: [
            "Las solicitudes usan el Formulario W-7 del IRS y requieren documentación de identidad — lo que tradicionalmente significaba enviar pasaportes originales por correo o esperar copias certificadas. Un Agente Tramitador Certificado (CAA) está autorizado por el IRS para verificar documentos en persona, así que los originales se quedan contigo.",
            "Aviah Financial funge como Agente Tramitador Certificado, lo que suele hacer el proceso más rápido, más seguro y mucho menos estresante.",
          ],
        },
        {
          h: "Una advertencia",
          p: [
            "Los ITIN pueden vencer si no se usan en una declaración federal durante varios años consecutivos, y la renovación sigue un proceso similar. Si crees que tú o un familiar pueden necesitar uno — o renovarlo — una consulta breve suele resolver la duda rápidamente.",
          ],
        },
      ],
      faqs: [
        { q: "¿Un ITIN me da estatus migratorio o autorización de trabajo?", a: "No. Un ITIN es estrictamente un número de procesamiento fiscal. No cambia el estatus migratorio y no autoriza el empleo." },
        { q: "¿Qué es un Agente Tramitador Certificado?", a: "Un CAA está autorizado por el IRS para verificar documentos de identidad en persona para solicitudes de ITIN, para que los solicitantes no tengan que enviar documentos originales como pasaportes por correo." },
      ],
    },
  },
  {
    slug: "escarling-martinez-the-human-vision-behind-aviah-financial",
    status: "published",
    category: "news",
    img: "staircase",
    date: "2026-06-23",
    updated: "2026-06-23",
    reviewed: "2026-06-23",
    reading: 6,
    author: AUTHOR,
    en: {
      title: "Escarling Martínez: The Human Vision Behind Aviah Financial",
      excerpt:
        "From an insurance desk at eighteen to hospital administration in Boston to founding a consulting practice in Lake Nona — the story of why Aviah exists.",
      content: [
        {
          h: "Starting at eighteen",
          p: [
            "Escarling Martínez entered the working world at 18, as an automobile insurance agent with MetLife. It was an early education in something textbooks rarely teach: behind every policy is a person trying to protect something that matters to them.",
            "That first role sparked an interest that never left — business, financial services, education, and helping people make informed decisions about their future.",
          ],
        },
        {
          h: "Boston: the corporate education",
          p: [
            "At the corporate offices of TJX Companies in Boston, Massachusetts, she advanced into business administration and served as an Import and Export Supervisor. The work demanded operations discipline, organization, leadership and a working fluency in international business.",
            "It was proof that systems — well-built ones — are what allow people to do their best work.",
          ],
        },
        {
          h: "Mass General: leadership under pressure",
          p: [
            "While continuing her higher education, Escarling accepted a position as an administrator within the Emergency Department at Mass General Hospital. Few environments test organization, communication and decision-making like a hospital emergency department.",
            "The experience connected her academic training with professional reality — and strengthened the discipline that later became a founding principle of her firm: precision over speed.",
          ],
        },
        {
          h: "The degree that tied it together",
          p: [
            "She graduated with a degree in Accounting and Business Administration — the formal complement to everything the years in insurance, corporate operations and healthcare administration had already taught her.",
            "Financial services, business, corporate operations, leadership and accounting: the combined foundation on which Aviah Financial was built.",
          ],
        },
        {
          h: "Why Aviah exists",
          p: [
            "Over the years, Escarling watched the same pattern repeat: people needed help across multiple areas of their financial lives, but had to visit a different professional for each — and the pieces never spoke to each other. Decisions were being made in isolation, and the cost of that isolation fell on the client.",
            "Aviah Financial was created as the alternative: one trusted relationship that begins with the complete picture. Her philosophy is the firm's: important decisions should never be made in isolation. For the Hispanic community of Orlando in particular — families and entrepreneurs building in a second language — that integrated, bilingual guidance is not a luxury. It is the difference between confusion and legacy.",
          ],
        },
      ],
      faqs: [
        { q: "Where is Aviah Financial located?", a: "In Lake Nona, Orlando — 6900 Tavistock Lakes Blvd, Suite 400, Orlando, FL 32827. Consultations are by appointment, in person or virtual." },
      ],
    },
    es: {
      title: "Escarling Martínez: La Visión Humana Detrás de Aviah Financial",
      excerpt:
        "De un escritorio de seguros a los dieciocho años a la administración hospitalaria en Boston y a fundar una firma de consultoría en Lake Nona — la historia de por qué existe Aviah.",
      content: [
        {
          h: "Comenzando a los dieciocho",
          p: [
            "Escarling Martínez entró al mundo laboral a los 18 años, como agente de seguros de automóviles en MetLife. Fue una educación temprana en algo que los libros rara vez enseñan: detrás de cada póliza hay una persona tratando de proteger algo que le importa.",
            "Ese primer rol despertó un interés que nunca la dejó — los negocios, los servicios financieros, la educación y ayudar a las personas a tomar decisiones informadas sobre su futuro.",
          ],
        },
        {
          h: "Boston: la educación corporativa",
          p: [
            "En las oficinas corporativas de TJX Companies en Boston, Massachusetts, avanzó a la administración de empresas y se desempeñó como Supervisora de Importación y Exportación. El trabajo exigía disciplina operativa, organización, liderazgo y fluidez práctica en negocios internacionales.",
            "Fue la prueba de que los sistemas — los bien construidos — son lo que permite a las personas hacer su mejor trabajo.",
          ],
        },
        {
          h: "Mass General: liderazgo bajo presión",
          p: [
            "Mientras continuaba su educación superior, Escarling aceptó un puesto como administradora en el Departamento de Emergencias de Mass General Hospital. Pocos entornos ponen a prueba la organización, la comunicación y la toma de decisiones como un departamento de emergencias hospitalario.",
            "La experiencia conectó su formación académica con la realidad profesional — y fortaleció la disciplina que luego se convirtió en principio fundacional de su firma: precisión sobre velocidad.",
          ],
        },
        {
          h: "El título que lo unió todo",
          p: [
            "Se graduó con una licenciatura en Contabilidad y Administración de Empresas — el complemento formal de todo lo que los años en seguros, operaciones corporativas y administración hospitalaria ya le habían enseñado.",
            "Servicios financieros, negocios, operaciones corporativas, liderazgo y contabilidad: la base combinada sobre la cual se construyó Aviah Financial.",
          ],
        },
        {
          h: "Por qué existe Aviah",
          p: [
            "Con los años, Escarling vio repetirse el mismo patrón: las personas necesitaban ayuda en múltiples áreas de su vida financiera, pero tenían que visitar un profesional diferente para cada una — y las piezas nunca se comunicaban. Las decisiones se tomaban de forma aislada, y el costo de ese aislamiento caía sobre el cliente.",
            "Aviah Financial fue creada como la alternativa: una sola relación de confianza que comienza con el panorama completo. Su filosofía es la de la firma: las decisiones importantes nunca deben tomarse de forma aislada. Para la comunidad hispana de Orlando en particular — familias y emprendedores construyendo en un segundo idioma — esa orientación integrada y bilingüe no es un lujo. Es la diferencia entre confusión y legado.",
          ],
        },
      ],
      faqs: [
        { q: "¿Dónde está ubicada Aviah Financial?", a: "En Lake Nona, Orlando — 6900 Tavistock Lakes Blvd, Suite 400, Orlando, FL 32827. Las consultas son con cita previa, presenciales o virtuales." },
      ],
    },
  },
  // ---------- Editorial drafts (structured, pending full copy) ----------
  {
    slug: "financial-mistakes-new-business-owners",
    status: "draft",
    category: "business",
    img: "office",
    date: "2026-07-01",
    updated: "2026-07-01",
    reviewed: "2026-07-01",
    reading: 6,
    author: AUTHOR,
    en: {
      title: "7 Financial Mistakes New Business Owners Should Avoid",
      excerpt: "Most new businesses don't fail from lack of effort — they fail from disorganization. Seven patterns we see most often, and how to avoid each.",
      outline: ["Mixing personal and business finances", "Choosing a structure by default", "No bookkeeping routine", "Ignoring tax obligations until April", "Pricing without knowing real costs", "Growing before the foundation exists", "Waiting too long to ask for guidance"],
    },
    es: {
      title: "7 Errores Financieros que los Nuevos Dueños de Negocio Deben Evitar",
      excerpt: "La mayoría de los negocios nuevos no fracasan por falta de esfuerzo — fracasan por desorganización. Siete patrones que vemos con más frecuencia, y cómo evitar cada uno.",
      outline: ["Mezclar finanzas personales y del negocio", "Elegir una estructura por defecto", "Sin rutina de contabilidad", "Ignorar las obligaciones fiscales hasta abril", "Fijar precios sin conocer los costos reales", "Crecer antes de que exista la base", "Esperar demasiado para pedir orientación"],
    },
  },
  {
    slug: "estate-planning-when-should-a-family-start",
    status: "draft",
    category: "estate",
    img: "staircase",
    date: "2026-07-06",
    updated: "2026-07-06",
    reviewed: "2026-07-06",
    reading: 5,
    author: AUTHOR,
    en: {
      title: "Estate Planning: When Should a Family Start?",
      excerpt: "Earlier than most people think. If you own assets, have children or run a business, there is already something to put in order.",
      outline: ["Why estate planning is about clarity now, not later", "The triggers: children, property, business, second countries", "What 'starting' actually looks like", "Documents vs. decisions — who does what", "How estate planning connects to taxes and protection"],
    },
    es: {
      title: "Planificación Patrimonial: ¿Cuándo Debería Comenzar una Familia?",
      excerpt: "Antes de lo que la mayoría cree. Si posees activos, tienes hijos o manejas un negocio, ya hay algo que poner en orden.",
      outline: ["Por qué la planificación patrimonial es claridad ahora, no después", "Las señales: hijos, propiedades, negocio, segundos países", "Cómo se ve realmente 'comenzar'", "Documentos vs. decisiones — quién hace qué", "Cómo la planificación patrimonial se conecta con impuestos y protección"],
    },
  },
  {
    slug: "organize-business-finances-before-tax-season",
    status: "draft",
    category: "business",
    img: "meeting",
    date: "2026-07-08",
    updated: "2026-07-08",
    reviewed: "2026-07-08",
    reading: 5,
    author: AUTHOR,
    en: {
      title: "How to Organize Your Business Finances Before Tax Season",
      excerpt: "Tax season is easy for businesses that stayed organized all year — and painful for the rest. A practical sequence to get ready before the deadline rush.",
      outline: ["Separate accounts: the non-negotiable first step", "A monthly bookkeeping rhythm that takes under an hour", "The documents your preparer will ask for", "Common deductions small businesses miss", "When to bring in professional support"],
    },
    es: {
      title: "Cómo Organizar las Finanzas de tu Negocio Antes de la Temporada de Impuestos",
      excerpt: "La temporada de impuestos es fácil para los negocios que se mantuvieron organizados todo el año — y dolorosa para el resto. Una secuencia práctica para prepararte antes de la prisa.",
      outline: ["Cuentas separadas: el primer paso innegociable", "Un ritmo mensual de contabilidad de menos de una hora", "Los documentos que tu preparador pedirá", "Deducciones comunes que los pequeños negocios pasan por alto", "Cuándo buscar apoyo profesional"],
    },
  },
  {
    slug: "starting-a-business-in-florida",
    status: "draft",
    category: "entrepreneurship",
    img: "hero",
    date: "2026-07-10",
    updated: "2026-07-10",
    reviewed: "2026-07-10",
    reading: 6,
    author: AUTHOR,
    en: {
      title: "Starting a Business in Florida: Financial Questions to Consider",
      excerpt: "Before the name, the logo and the launch — the financial questions that decide whether the business stands on rock or sand.",
      outline: ["Which structure fits your situation — and why it matters", "EIN, registrations and the order of operations", "Separating money from day one", "Taxes you didn't know applied to you", "Building the advisory relationship before you need it"],
    },
    es: {
      title: "Iniciar un Negocio en Florida: Preguntas Financieras a Considerar",
      excerpt: "Antes del nombre, el logo y el lanzamiento — las preguntas financieras que deciden si el negocio se para sobre roca o sobre arena.",
      outline: ["Qué estructura encaja con tu situación — y por qué importa", "EIN, registros y el orden de las operaciones", "Separar el dinero desde el primer día", "Impuestos que no sabías que te aplicaban", "Construir la relación de asesoría antes de necesitarla"],
    },
  },
  {
    slug: "financial-education-family-future",
    status: "draft",
    category: "education",
    img: "marble",
    date: "2026-07-13",
    updated: "2026-07-13",
    reviewed: "2026-07-13",
    reading: 4,
    author: AUTHOR,
    en: {
      title: "How Financial Education Can Change the Future of a Family",
      excerpt: "Money habits are inherited — but they can also be chosen. What changes when a family decides to understand its finances instead of endure them.",
      outline: ["The inheritance nobody talks about: financial habits", "First-generation builders and the information gap", "Teaching children by example, not lecture", "Small structural decisions with generational impact", "Where to begin as a family"],
    },
    es: {
      title: "Cómo la Educación Financiera Puede Cambiar el Futuro de una Familia",
      excerpt: "Los hábitos de dinero se heredan — pero también se pueden elegir. Qué cambia cuando una familia decide entender sus finanzas en lugar de soportarlas.",
      outline: ["La herencia de la que nadie habla: los hábitos financieros", "Los constructores de primera generación y la brecha de información", "Enseñar a los hijos con el ejemplo, no con sermones", "Pequeñas decisiones estructurales con impacto generacional", "Por dónde empezar como familia"],
    },
  },
  {
    slug: "business-structure-and-taxes",
    status: "draft",
    category: "entrepreneurship",
    img: "office",
    date: "2026-07-15",
    updated: "2026-07-15",
    reviewed: "2026-07-15",
    reading: 6,
    author: AUTHOR,
    en: {
      title: "Business Structure and Taxes: Questions Every Entrepreneur Should Ask",
      excerpt: "LLC, S-corp, sole proprietorship — the labels matter less than what they do to your taxes, your liability and your future options.",
      outline: ["What a structure actually changes", "How each option is taxed — in plain language", "Liability: what separation really protects", "When the structure you have stops fitting", "Questions to bring to your consultation"],
    },
    es: {
      title: "Estructura Empresarial e Impuestos: Preguntas que Todo Emprendedor Debe Hacer",
      excerpt: "LLC, S-corp, propietario único — las etiquetas importan menos que lo que hacen con tus impuestos, tu responsabilidad y tus opciones futuras.",
      outline: ["Qué cambia realmente una estructura", "Cómo se grava cada opción — en lenguaje claro", "Responsabilidad: qué protege realmente la separación", "Cuándo la estructura que tienes deja de encajar", "Preguntas para llevar a tu consulta"],
    },
  },
  {
    slug: "financial-decisions-not-in-isolation",
    status: "draft",
    category: "education",
    img: "staircase",
    date: "2026-07-17",
    updated: "2026-07-17",
    reviewed: "2026-07-17",
    reading: 4,
    author: AUTHOR,
    en: {
      title: "Why Financial Decisions Should Not Be Made in Isolation",
      excerpt: "A tax decision becomes a business decision becomes an estate decision. The cost of fragmented advice is paid by the client — every time.",
      outline: ["The anatomy of an isolated decision", "How taxes, business and estate pull on the same rope", "What 'integrated guidance' actually means", "A real-world pattern: the new business owner", "One relationship vs. five phone calls"],
    },
    es: {
      title: "Por Qué las Decisiones Financieras No Deben Tomarse de Forma Aislada",
      excerpt: "Una decisión fiscal se convierte en decisión de negocio, que se convierte en decisión patrimonial. El costo del consejo fragmentado lo paga el cliente — siempre.",
      outline: ["La anatomía de una decisión aislada", "Cómo impuestos, negocio y patrimonio tiran de la misma cuerda", "Qué significa realmente 'orientación integrada'", "Un patrón del mundo real: el nuevo dueño de negocio", "Una relación vs. cinco llamadas telefónicas"],
    },
  },
  {
    slug: "preparing-financially-for-long-term-family-goals",
    status: "draft",
    category: "education",
    img: "meeting",
    date: "2026-07-20",
    updated: "2026-07-20",
    reviewed: "2026-07-20",
    reading: 5,
    author: AUTHOR,
    en: {
      title: "Preparing Financially for Long-Term Family Goals",
      excerpt: "College, a home, caring for parents, a business for the children — long-term goals need structure, not just savings.",
      outline: ["Naming the goals before funding them", "The order of operations: protection, then growth", "Multi-country families and double planning", "Making goals visible to the whole family", "Reviewing the plan once a year"],
    },
    es: {
      title: "Prepararse Financieramente para las Metas Familiares a Largo Plazo",
      excerpt: "La universidad, una casa, cuidar a los padres, un negocio para los hijos — las metas a largo plazo necesitan estructura, no solo ahorros.",
      outline: ["Nombrar las metas antes de financiarlas", "El orden de las operaciones: protección, luego crecimiento", "Familias en varios países y la doble planificación", "Hacer las metas visibles para toda la familia", "Revisar el plan una vez al año"],
    },
  },
  {
    slug: "when-to-speak-with-a-financial-professional",
    status: "draft",
    category: "education",
    img: "hero",
    date: "2026-07-22",
    updated: "2026-07-22",
    reviewed: "2026-07-22",
    reading: 4,
    author: AUTHOR,
    en: {
      title: "When Should You Speak With a Financial or Business Professional?",
      excerpt: "Not when something breaks. The best time is earlier — at the moments this article lists, before decisions harden into consequences.",
      outline: ["The moments that deserve a conversation", "Starting, buying, selling, inheriting", "What a first consultation actually costs you: an hour", "What to bring and what to expect", "The question that tells you it's time"],
    },
    es: {
      title: "¿Cuándo Deberías Hablar con un Profesional Financiero o Empresarial?",
      excerpt: "No cuando algo se rompe. El mejor momento es antes — en los momentos que este artículo enumera, antes de que las decisiones se endurezcan en consecuencias.",
      outline: ["Los momentos que merecen una conversación", "Iniciar, comprar, vender, heredar", "Lo que realmente cuesta una primera consulta: una hora", "Qué llevar y qué esperar", "La pregunta que te dice que ya es momento"],
    },
  },
];

export const getPost = (slug) => POSTS.find((p) => p.slug === slug);
export const publishedPosts = POSTS.filter((p) => p.status === "published");
